import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/shared/components/ui/table';

const normalizeSearchValue = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const normalizePostalSearchValue = (value) => normalizeSearchValue(value).replace(/\s+/g, '');

const formatDistance = (value) => `${Number(value || 0).toFixed(2)} km`;

const pageSizeOptions = ['10', '25', '50', 'all'];

const RadiusMembersDialog = ({
  open,
  onOpenChange,
  partnerName,
  radiusKm,
  radiusResult
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [distanceFilter, setDistanceFilter] = useState('all');
  const [pageSize, setPageSize] = useState('10');
  const [page, setPage] = useState(1);

  const rows = useMemo(() => radiusResult?.rows || [], [radiusResult]);
  const hasMemberNames = useMemo(() => rows.some((row) => Boolean(row.memberName)), [rows]);
  const isEstimate = radiusResult?.basis?.includes('estimate');
  const resultLabel = isEstimate ? `${(radiusResult?.members || 0).toLocaleString()} est.` : (radiusResult?.members || 0).toLocaleString();
  const recordLabel = hasMemberNames ? 'members' : 'postal codes';

  const filteredRows = useMemo(() => {
    const textQuery = normalizeSearchValue(searchQuery.trim());
    const postalQuery = normalizePostalSearchValue(searchQuery.trim());

    return rows.filter((row) => {
      if (distanceFilter !== 'all') {
        const maxDistance = Number(distanceFilter);
        if (Number.isFinite(maxDistance) && row.distanceKm > maxDistance) return false;
      }

      if (!textQuery) return true;

      const searchable = [
        row.memberName,
        row.name,
        row.postalCode,
        row.members,
        row.status,
        formatDistance(row.distanceKm)
      ].map(normalizeSearchValue).join(' ');
      const postalSearchable = [
        row.memberName,
        row.name,
        row.postalCode
      ].map(normalizePostalSearchValue).join(' ');

      return searchable.includes(textQuery) || postalSearchable.includes(postalQuery);
    });
  }, [rows, searchQuery, distanceFilter]);

  const computedPageSize = pageSize === 'all' ? filteredRows.length || 1 : Number(pageSize);
  const pageCount = Math.max(Math.ceil(filteredRows.length / computedPageSize), 1);
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * computedPageSize;
  const pageRows = filteredRows.slice(pageStart, pageStart + computedPageSize);
  const showingStart = filteredRows.length === 0 ? 0 : pageStart + 1;
  const showingEnd = Math.min(pageStart + pageRows.length, filteredRows.length);

  const clearFilters = () => {
    setSearchQuery('');
    setDistanceFilter('all');
    setPageSize('10');
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleDistanceChange = (value) => {
    setDistanceFilter(value);
    setPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setPage(1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-5 border-b bg-background">
          <DialogTitle className="text-xl">
            Members in Radius - {partnerName || 'Selected partner'}
          </DialogTitle>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <div className="rounded-md border bg-white px-3 py-1 font-medium">Radius: {radiusKm || '-'} km</div>
            <div className="rounded-md border bg-white px-3 py-1 font-medium">Members: {resultLabel}</div>
          </div>
        </DialogHeader>

        <div className="px-6 py-4 border-b bg-muted/20">
          <div className="grid gap-2 lg:grid-cols-[minmax(260px,1fr)_180px_120px_auto]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder={hasMemberNames ? 'Search by member name or postal code...' : 'Search by postal code...'}
                className="pl-9 h-9"
              />
            </div>
            <Select value={distanceFilter} onValueChange={handleDistanceChange}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Distance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All distances</SelectItem>
                <SelectItem value="1">Within 1 km</SelectItem>
                <SelectItem value="2">Within 2 km</SelectItem>
                <SelectItem value="5">Within 5 km</SelectItem>
              </SelectContent>
            </Select>
            <Select value={pageSize} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Rows" />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option === 'all' ? 'All rows' : `${option} / page`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" className="h-9 gap-2" onClick={clearFilters}>
              <X className="h-4 w-4" />
              Clear
            </Button>
          </div>
        </div>

        <ScrollArea className="max-h-[48vh]">
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow>
                {hasMemberNames && <TableHead>Member Name</TableHead>}
                <TableHead>Postal code</TableHead>
                {hasMemberNames && <TableHead>Status</TableHead>}
                {!hasMemberNames && <TableHead className="text-right">Members</TableHead>}
                <TableHead className="text-right">Distance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((row) => (
                <TableRow key={row.id}>
                  {hasMemberNames && (
                    <TableCell>
                      <div className="font-medium">{row.memberName || '-'}</div>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="font-medium">{row.postalCode || row.name || '-'}</div>
                  </TableCell>
                  {hasMemberNames && <TableCell>{row.status || '-'}</TableCell>}
                  {!hasMemberNames && (
                    <TableCell className="text-right font-semibold">{row.members.toLocaleString()}</TableCell>
                  )}
                  <TableCell className="text-right tabular-nums">{formatDistance(row.distanceKm)}</TableCell>
                </TableRow>
              ))}
              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={hasMemberNames ? 4 : 3} className="h-24 text-center text-muted-foreground">
                    No {recordLabel} match the current search or filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t bg-background sm:justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {showingStart}-{showingEnd} of {filteredRows.length.toLocaleString()} {recordLabel}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={currentPage <= 1}
              onClick={() => setPage(Math.max(currentPage - 1, 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={currentPage >= pageCount}
              onClick={() => setPage(Math.min(currentPage + 1, pageCount))}
            >
              Next
            </Button>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RadiusMembersDialog;
